import { Capacitor } from "@src-capacitor/@capacitor/core"
import { AxiosError, CanceledError, isAxiosError } from "axios"
import { springboardAPI } from "src/boot/axios"
import { i18n } from "src/boot/i18n"
import { getCommonDB } from "src/db/databases"
import { useCacheStore } from "src/stores/cacheStore"
import { Log } from "src/util/classes/log"
import { CACHE_AVATARS } from "src/util/constants/configurations"
import { SERVER_ERROR_INCORRECT_PASSWORD } from "src/util/constants/serverErrors"
import { SpringboardURL } from "src/util/enums/urls"
import { getUser, getUserImage } from "src/util/http/springboard/user"
import { UserAvatar, UserBasic, UserWithAvatar } from "src/util/types/dataModels/user"

export function getPasswordRequirements(currentPassword: string) {
  const { t } = i18n.global

  return [
    (val: string) => /[A-Z]/.test(val) || t("sfxDialogChangePassword.requirement_one_uppercase"),
    (val: string) => /[a-z]/.test(val) || t("sfxDialogChangePassword.requirement_one_lowercase"),
    (val: string) => /\d/.test(val) || t("sfxDialogChangePassword.requirement_one_number"),
    (val: string) =>
      /[^\dA-Za-z]/.test(val) || t("sfxDialogChangePassword.requirement_one_special_char"),
    (val: string) => val.length > 10 || t("sfxDialogChangePassword.requirement_min_char"),
    (val: string) => val.length < 128 || t("sfxDialogChangePassword.requirement_max_char"),
    (val: string) =>
      val !== currentPassword || t("sfxDialogChangePassword.requirement_same_as_old"),
  ]
}

export function isIncorrectPassword(error: AxiosError): boolean {
  const errorMessage: Record<string, unknown> | undefined = error.response?.data as
    | Record<string, unknown>
    | undefined

  return (
    Boolean(errorMessage) &&
    Array.isArray(errorMessage?.current_password) &&
    errorMessage?.current_password[0] === SERVER_ERROR_INCORRECT_PASSWORD
  )
}

export async function getUserData(userId: string): Promise<UserBasic | undefined> {
  try {
    const cacheStore = useCacheStore()
    const userInCache = cacheStore.user.get(userId)

    if (userInCache) {
      return userInCache
    } else {
      const db = await getCommonDB()
      const userInDB = await db.user.findOne(userId).exec()

      if (userInDB) {
        const userMutable = userInDB.toMutableJSON()
        cacheStore.user.set(userMutable.id, userMutable)
        return userMutable
      }

      return await getUser({}, userId)
    }
  } catch (error) {
    Log.error({
      message: "An error occurred while fetching User data",
      error,
    })
  }
}

export async function getUserWithAvatarData(userId: string): Promise<UserWithAvatar | undefined> {
  try {
    const userData: UserWithAvatar | undefined = await getUserData(userId)
    if (userData) {
      return addUserAvatarToUser(userData)
    }
  } catch (error) {
    Log.error({
      message: "An error occurred while fetching User with Avatar data",
      error,
    })
  }
}

export async function addUserAvatarToUser(user: UserBasic): Promise<UserWithAvatar> {
  const avatar = await getUserAvatarData(user.id)
  return { ...user, avatar }
}

export async function getUserAvatarData(userId: string): Promise<string | undefined> {
  const cacheStore = useCacheStore()
  let userAvatar: UserAvatar | undefined
  const avatarInCache = cacheStore.user_avatar.get(userId)
  const db = await getCommonDB()

  if (avatarInCache) {
    userAvatar = avatarInCache
  } else {
    const avatarInDB = await db.user_avatar.findOne(userId).exec()

    if (avatarInDB) {
      cacheStore.user_avatar.set(userId, avatarInDB)
      userAvatar = avatarInDB
    }
  }

  return process.env.MODE === "capacitor"
    ? getUserAvatarDataCapacitor(userId, userAvatar)
    : getUserAvatarDataWeb(userId, userAvatar)
}

export async function saveUserAvatar(userId: string, avatar: Blob) {
  return process.env.MODE === "capacitor"
    ? saveUserAvatarCapacitor(userId, avatar)
    : saveUserAvatarWeb(userId, avatar)
}

async function getUserAvatarDataCapacitor(
  userId: string,
  userAvatar?: UserAvatar
): Promise<string | undefined> {
  if (userAvatar?.path) {
    return Capacitor.convertFileSrc(userAvatar.path)
  }

  const newUserAvatar = await saveUserAvatarCapacitor(userId)
  if (newUserAvatar) return Capacitor.convertFileSrc(newUserAvatar.path)
}

async function saveUserAvatarCapacitor(userId: string, avatar?: Blob) {
  try {
    const file = avatar || (await getUserImage({}, userId))

    if (!file) return

    const { Directory } = await import("@src-capacitor/@capacitor/filesystem/dist/esm/definitions")
    const write_blob = await import("@src-capacitor/capacitor-blob-writer")
    const db = await getCommonDB()

    const fileDir = await write_blob.default({
      path: userId,
      blob: file,
      directory: Directory.Data,
      recursive: true,
    })

    return await db.user_avatar.upsert({ id: userId, type: file.type, path: fileDir })
  } catch (error) {
    // Request was canceled or the user doesn't have an avatar. No point doing anything
    if (error instanceof CanceledError || (isAxiosError(error) && error.response?.status === 404)) {
      return
    }

    Log.error({ message: "Could not download user avatar", error, data: { userId } })
  }
}

async function getUserAvatarDataWeb(
  userId: string,
  userAvatar?: UserAvatar
): Promise<string | undefined> {
  const cache = await caches.open(CACHE_AVATARS)

  if (userAvatar?.path) {
    const foundImage = await cache.match(userAvatar.path)
    if (foundImage) {
      const blob = await foundImage.blob()
      return URL.createObjectURL(blob)
    }
  }

  const newUserAvatar = await saveUserAvatarWeb(userId)
  if (newUserAvatar) {
    const foundImage = await cache.match(newUserAvatar.path)
    if (foundImage) {
      const blob = await foundImage.blob()
      return URL.createObjectURL(blob)
    }
  }
}

async function saveUserAvatarWeb(userId: string, avatar?: Blob) {
  try {
    const file = avatar || (await getUserImage({}, userId))

    if (!file) return

    const db = await getCommonDB()
    const path = getWebUserAvatarURL(userId)
    const cache = await caches.open(CACHE_AVATARS)
    await cache.put(path, new Response(file))
    return await db.user_avatar.upsert({ id: userId, type: file.type, path })
  } catch (error) {
    // Request was canceled or the user doesn't have an avatar. No point doing anything
    if (error instanceof CanceledError || (isAxiosError(error) && error.response?.status === 404)) {
      return
    }

    Log.error({ message: "Could not download user avatar", error, data: { userId } })
  }
}

function getWebUserAvatarURL(userId: string) {
  const base = springboardAPI.defaults.baseURL
  const url = SpringboardURL.USER_ID_IMAGE.replace("{id}", userId)
  return base + url
}
